import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  OAUTH_POPUP_MESSAGE_SOURCE,
  OAuthPopupMessage,
  recoverOAuthParams,
} from '@ninety/web/pages/shared/integrations';

/** 'google-calendar' -> 'Google Calendar'. Good enough for a message that's on screen for a beat. */
function humanizeProvider(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** A repeated query key arrives as an array; use the first value, as queryParamMap.get() does. */
function firstValue(value: string | string[] | null | undefined): string | null {
  return (Array.isArray(value) ? value[0] : value) ?? null;
}

/**
 * Landing page for a popup-window OAuth connect flow. Relays the provider's redirect query
 * params back to the opener via postMessage, then closes itself - the opener does the actual
 * token exchange, so this page never needs to be authenticated or make HTTP calls. Shared across
 * every provider (calendar today, others later) - the connecting message is derived from the
 * `provider` query param rather than naming any one integration.
 */
@Component({
  selector: 'ninety-oauth-popup-callback',
  standalone: true,
  templateUrl: './oauth-popup-callback.component.html',
  styleUrl: './oauth-popup-callback.component.scss',
})
export class OauthPopupCallbackComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);

  providerLabel = 'integration';

  ngOnInit(): void {
    // Monday merges its response onto our `?provider=` query with another `?`, so the code can land inside `provider`.
    const params = recoverOAuthParams(this.route.snapshot.queryParams);
    const provider = firstValue(params['provider']);
    const code = firstValue(params['code']);
    const state = firstValue(params['state']);
    const error = firstValue(params['error']);

    if (provider) {
      this.providerLabel = humanizeProvider(provider);
    }

    const message: OAuthPopupMessage =
      provider && code
        ? { source: OAUTH_POPUP_MESSAGE_SOURCE, provider, code, state }
        : { source: OAUTH_POPUP_MESSAGE_SOURCE, error: error || 'missing_code' };

    window.opener?.postMessage(message, window.location.origin);
    window.close();
  }
}
